import {useState} from "react";
import {
    Button,
    Chip,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {CheckCircle, FileCopy} from "@mui/icons-material";
import moment from "moment";
import {useAsync2, useAsyncAction} from "nate-react-api-helpers";
import {Dialog2} from "./Dialog2";
import {api} from "../api/API";
import {ApiToken, CreatedApiToken} from "../api/ApiTokens";
import {green} from "@mui/material/colors";

export function ApiKeysDialog(props: {
    onClose(): void;
}) {
    const list = useAsync2(() => api.apiTokens.list({includeArchived: true}), {}, []);
    const [creating, setCreating] = useState(false);
    const [created, setCreated] = useState<CreatedApiToken | null>(null);

    return (
        <Dialog2 open onClose={() => props.onClose()} maxWidth="sm" fullWidth>
            <DialogTitle>API Keys</DialogTitle>

            {created ? (
                <NewKeyView created={created} onDone={() => setCreated(null)} />
            ) : creating ? (
                <CreateKeyView
                    onCancel={() => setCreating(false)}
                    onCreated={c => {
                        setCreating(false);
                        setCreated(c);
                        list.reload();
                    }}
                />
            ) : (
                <>
                    <DialogContent>
                        <Typography variant="body2" color="textSecondary">
                            API keys let external tools (like Claude Desktop via MCP) act on your behalf.
                            Treat them like passwords.
                        </Typography>
                        <div style={{height: 12}} />

                        {list.LoadingOrErrorElement}

                        {list.result && list.result.length === 0 && (
                            <Typography variant="body2" color="textSecondary">
                                You don't have any API keys yet.
                            </Typography>
                        )}

                        {list.result && list.result.map(k => (
                            <KeyRow key={k.id} token={k} onChanged={() => list.reload()} />
                        ))}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => props.onClose()}>Close</Button>
                        <Button variant="contained" color="primary" onClick={() => setCreating(true)}>
                            New API Key
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog2>
    );
}

function isExpired(token: ApiToken) {
    return moment(token.expiresAt).isBefore(moment());
}

function KeyRow(props: {
    token: ApiToken;
    onChanged(): void;
}) {
    const token = props.token;
    const expired = isExpired(token);
    const dead = token.archived || expired;
    const [revoking, setRevoking] = useState(false);
    const [reason, setReason] = useState("");

    const revoke = useAsyncAction(async () => {
        await api.apiTokens.revoke({id: token.id, archived: true, archivedReason: reason});
        props.onChanged();
    }, [token.id, reason]);

    return (
        <div style={{
            padding: "8px 0",
            opacity: dead ? 0.5 : 1,
        }}>
            <Divider />
            <div style={{height: 8}} />
            <div style={{display: "flex", alignItems: "center", gap: 8}}>
                <div style={{flex: 1, minWidth: 0}}>
                    <div style={{display: "flex", alignItems: "center", gap: 8}}>
                        <Typography variant="subtitle2" style={{textDecoration: dead ? "line-through" : undefined}}>
                            {token.name || "(unnamed)"}
                        </Typography>
                        {token.archived && <Chip size="small" label="Revoked" color="default" />}
                        {!token.archived && expired && <Chip size="small" label="Expired" color="default" />}
                    </div>
                    <Typography variant="caption" color="textSecondary" component="div">
                        <code>{token.prefix}…</code>
                        {" · "}
                        {token.archived
                            ? <>revoked {moment(token.archivedAt).format("YYYY-MMM-D")}{token.archivedReason ? ` — ${token.archivedReason}` : ""}</>
                            : expired
                                ? <>expired {moment(token.expiresAt).format("YYYY-MMM-D")}</>
                                : <>expires {moment(token.expiresAt).format("YYYY-MMM-D")}</>}
                        {token.lastUsedAt && !token.archived && <> · last used {moment(token.lastUsedAt).fromNow()}</>}
                    </Typography>
                </div>
                {!dead && !revoking && (
                    <Button size="small" color="error" onClick={() => setRevoking(true)}>Revoke</Button>
                )}
            </div>

            {revoking && (
                <div style={{marginTop: 8, display: "flex", flexDirection: "column", gap: 8}}>
                    <TextField
                        size="small"
                        label="Reason for revoking"
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        autoFocus
                        fullWidth
                    />
                    {revoke.LoadingElement}
                    <div style={{display: "flex", justifyContent: "flex-end", gap: 8}}>
                        <Button size="small" onClick={() => setRevoking(false)}>Cancel</Button>
                        <Button
                            size="small"
                            variant="contained"
                            color="error"
                            disabled={!reason.trim() || revoke.loading}
                            onClick={() => revoke.callback(null)}
                        >
                            Revoke key
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function CreateKeyView(props: {
    onCancel(): void;
    onCreated(c: CreatedApiToken): void;
}) {
    const [name, setName] = useState("");
    const [expiresAt, setExpiresAt] = useState(moment().add(1, "year").format("YYYY-MM-DD"));

    const create = useAsyncAction(async () => {
        const result = await api.apiTokens.create({
            name: name.trim(),
            // send at start of the selected day so it stays within the 1yr server cap
            expiresAt: moment(expiresAt, "YYYY-MM-DD").toISOString(),
        });
        props.onCreated(result);
    }, [name, expiresAt]);

    const submit = () => {
        if (!name.trim()) return;
        create.callback(null);
    };

    return (
        <>
            <DialogContent>
                <div style={{display: "flex", flexDirection: "column", gap: 16, paddingTop: 4}}>
                    <TextField
                        label="Name"
                        placeholder="e.g. Claude Desktop"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        autoFocus
                        required
                        fullWidth
                    />
                    <TextField
                        label="Expires"
                        type="date"
                        value={expiresAt}
                        onChange={e => setExpiresAt(e.target.value)}
                        InputLabelProps={{shrink: true}}
                        inputProps={{
                            min: moment().add(1, "day").format("YYYY-MM-DD"),
                            max: moment().add(1, "year").format("YYYY-MM-DD"),
                        }}
                        helperText="Maximum 1 year from today"
                        fullWidth
                    />
                    {create.LoadingElement}
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => props.onCancel()}>Cancel</Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={!name.trim() || create.loading}
                    onClick={submit}
                >
                    {create.loading ? <CircularProgress size={20} /> : "Create"}
                </Button>
            </DialogActions>
        </>
    );
}

function NewKeyView(props: {
    created: CreatedApiToken;
    onDone(): void;
}) {
    const token = props.created.token;

    return (
        <>
            <DialogContent>
                <Typography variant="subtitle2" color="error">
                    Copy this key now — it won't be shown again.
                </Typography>
                <div style={{height: 8}} />
                <CopyField label="API Key" value={token} />

                <div style={{height: 24}} />
                <Divider />
                <div style={{height: 16}} />

                <Typography variant="subtitle2">Connect Claude Desktop (MCP)</Typography>
                <div style={{height: 4}} />
                <Typography variant="body2" color="textSecondary">
                    Paste this prompt into Claude:
                </Typography>
                <div style={{height: 8}} />
                <CopyField label="Prompt" value={claudePrompt(token)} multiline />
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="primary" onClick={() => props.onDone()}>Done</Button>
            </DialogActions>
        </>
    );
}

function claudePrompt(token: string) {
    const url = window.location.origin + "/api/mcp";
    const json = JSON.stringify({
        mcpServers: {
            handle: {
                type: "http",
                url: url,
                headers: {
                    Authorization: "Bearer " + token,
                },
            },
        },
    }, null, 2);
    return "add this mcp server " + json;
}

function CopyField(props: {
    label: string;
    value: string;
    multiline?: boolean;
}) {
    const [copied, setCopied] = useState(false);

    const copy = () => {
        navigator.clipboard.writeText(props.value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <TextField
            label={props.label}
            value={props.value}
            multiline={props.multiline}
            fullWidth
            InputProps={{
                readOnly: true,
                style: props.multiline ? {fontFamily: "monospace", fontSize: "0.75rem"} : {fontFamily: "monospace"},
                endAdornment: (
                    <Tooltip title={copied ? "Copied!" : "Copy"}>
                        <IconButton onClick={copy} style={{alignSelf: "flex-start"}}>
                            {copied ? <CheckCircle style={{color: green["700"]}} /> : <FileCopy />}
                        </IconButton>
                    </Tooltip>
                ),
            }}
        />
    );
}
